import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MoodService } from 'src/app/services/mood.service';
import { MoodModalDataComponent } from '../mood-modal-data/mood-modal-data.component';
import { MoodCalendarDay, MoodDayStatus, Moods, MoodsDefaultvalues, MoodViewModel } from '../moods';
import { ToastService } from 'src/app/services/toast-service.service';
import { TranslationService } from 'src/app/services/translation.service';
import { LanguageService } from 'src/app/services/language.service';
import { ApplicationLanguages } from 'src/app/components/language/language';

@Component({
    selector: 'app-mood-checkin',
    templateUrl: './mood-checkin.component.html',
    styleUrls: ['./mood-checkin.component.scss'],
    standalone: false
})
export class MoodCheckinComponent implements OnInit {

  registeredMoods: MoodViewModel[] = [];
  calendarDays: MoodCalendarDay[] = [];
  selectedMood: Moods = MoodsDefaultvalues();
  translations: any;
  showMoodLogged = false;
  loggedCount = 0;
  daysInMonth = 0;
  currentMonthName = '';
  weekdayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  moodScale: any[] = [];

  constructor(
    private _moodservice: MoodService,
    private dialog: MatDialog,
    private toastservice: ToastService,
    private translationservice: TranslationService,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.buildCalendarMeta();
    this.loadMoodScale();
    this.getRegisteredMoods();
    this.translationservice.getTranslation().subscribe({
      next: (translations) => {
        this.translations = translations;
      }
    });
  }

  getMoodScaleItem(id: number) {
    return this.moodScale.find(mood => mood.id === id);
  }

  getDayStyle(cell: MoodCalendarDay): Record<string, string> {
    if (cell.mood?.color) {
      return { 'background-color': cell.mood.color };
    }
    return {};
  }

  loadMoodScale() {
    this._moodservice.getAllMoods().subscribe({
      next: (data) => {
        this.moodScale = data;
      }
    });
  }

  get journalsLabel(): string {
    const prefix = this.translations?.moodJournalsWrittenInText ?? 'Journals written in';
    return `${prefix} ${this.currentMonthName}`;
  }

  getRegisteredMoods() {
    this._moodservice.getRegisteredMoods().subscribe({
      next: (resp) => {
        this.registeredMoods = resp;
        this.buildCalendarDays();
        this.isMoodRegisteredForToday();
      }
    });
  }

  isMoodRegisteredForToday() {
    this._moodservice.isMoodRegisteredForToday().subscribe({
      next: (resp) => {
        this.showMoodLogged = resp.status;
      }
    });
  }

  openRegisterMoodModal() {
    if (this.showMoodLogged) {
      return;
    }

    const dialogRef = this.dialog.open(MoodModalDataComponent, {
      data: { showDesc: false },
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'mood-dialog-panel'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result != null) {
        this.selectedMood.mood = result.mood;
        this.selectedMood.moodJournal = result.moodJournal;
        this._moodservice.registerMood(this.selectedMood).subscribe({
          next: (data) => {
            if (data.status) {
              this.getRegisteredMoods();
            } else {
              this.toastservice.showErrorMessage("Today's Mood is already registered");
            }
          }
        });
      }
    });
  }

  openDescModal(registeredMood: MoodViewModel) {
    this.dialog.open(MoodModalDataComponent, {
      data: {
        showDesc: true,
        moodId: registeredMood.moodId,
        moodColor: registeredMood.color,
        desc: registeredMood.moodJournal,
        date: registeredMood.createdDate
      },
      width: '520px',
      maxWidth: '95vw',
      panelClass: 'mood-dialog-panel'
    });
  }

  onDayClick(day: MoodCalendarDay) {
    if (day.mood) {
      this.openDescModal(day.mood);
    } else if (!day.isFuture && !day.isPadding && !this.showMoodLogged) {
      this.openRegisterMoodModal();
    }
  }

  private buildCalendarMeta() {
    const today = new Date();
    this.daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const isFrench = this.languageService.getCurretnLang() === ApplicationLanguages.FRENCH;
    this.currentMonthName = today.toLocaleString(isFrench ? 'fr-CA' : 'en-CA', { month: 'long' });
  }

  private buildCalendarDays() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const moodByDate = new Map<string, MoodViewModel>();

    this.registeredMoods.forEach(mood => {
      const dateKey = mood.createdOnDate || this.parseCreatedDate(mood.createdDate, year, month);
      if (dateKey) {
        moodByDate.set(dateKey, mood);
      }
    });

    this.loggedCount = moodByDate.size;
    this.calendarDays = [];

    for (let i = 0; i < startOffset; i++) {
      this.calendarDays.push(this.createPaddingDay());
    }

    for (let day = 1; day <= this.daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateKey = this.toDateKey(date);
      const mood = moodByDate.get(dateKey);
      const isToday = this.isSameDay(date, today);
      const isFuture = date > new Date(today.getFullYear(), today.getMonth(), today.getDate());

      this.calendarDays.push({
        day,
        isPadding: false,
        mood,
        isToday,
        isFuture,
        status: this.resolveDayStatus(mood, isFuture, date, today)
      });
    }
  }

  private createPaddingDay(): MoodCalendarDay {
    return {
      day: 0,
      isPadding: true,
      isToday: false,
      isFuture: false,
      status: 'skipped'
    };
  }

  private resolveDayStatus(mood: MoodViewModel | undefined, isFuture: boolean, date: Date, today: Date): MoodDayStatus {
    if (isFuture) {
      return 'future';
    }

    if (!mood) {
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      return date <= todayStart ? 'skipped' : 'future';
    }

    return 'logged';
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
  }

  private parseCreatedDate(createdDate: string, year: number, month: number): string | null {
    if (!createdDate) {
      return null;
    }

    const parsed = new Date(`${createdDate} ${year}`);
    if (Number.isNaN(parsed.getTime()) || parsed.getMonth() !== month) {
      return null;
    }

    return this.toDateKey(parsed);
  }
}
